import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { freeFetch, inputClass, labelClass } from './api';
import { useFreeAuth } from './FreeAuth';

const btn = 'font-heading font-bold text-sm uppercase tracking-wider rounded-none px-6 py-3 transition-colors';

function DeleteAccount({ email }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const remove = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await freeFetch('/account/delete', { method: 'POST', body: JSON.stringify({ confirm: typed }) });
      // The confirmation page signs the app out on mount; doing it here would let
      // the signed-in layout redirect to /free before this navigation lands.
      navigate('/free/account-deleted', { replace: true, state: { deleted: true, email, emailSent: data.email_sent } });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <section className="border border-mono-line bg-mono-card p-6 md:p-8" data-testid="account-delete-section">
      <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-white mb-2">Delete my account</h2>
      <p className="text-mono-mute font-body text-sm leading-relaxed mb-5">
        This permanently deletes your account, every session you have logged, your check-ins and any login links.
        It cannot be undone.
      </p>
      {!confirming ? (
        <button type="button" onClick={() => setConfirming(true)} className={`${btn} border border-crimson text-crimson hover:bg-crimson hover:text-white`} data-testid="account-delete-start-btn">
          Delete my account
        </button>
      ) : (
        <div data-testid="account-delete-confirm">
          <label className={labelClass} htmlFor="delete-confirm-input">Type DELETE to confirm</label>
          <input
            id="delete-confirm-input"
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            autoCapitalize="characters"
            className={inputClass + ' mb-4'}
            data-testid="account-delete-confirm-input"
          />
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={remove}
              disabled={typed !== 'DELETE' || busy}
              className={`${btn} bg-crimson text-white hover:bg-crimson-bright disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid="account-delete-confirm-btn"
            >
              {busy ? 'Deleting...' : 'Permanently delete'}
            </button>
            <button type="button" onClick={() => { setConfirming(false); setTyped(''); }} disabled={busy} className={`${btn} border border-mono-line text-mono-mute hover:text-white`} data-testid="account-delete-cancel-btn">
              Cancel
            </button>
          </div>
          {error && <p className="text-red-400 text-sm font-body mt-3" data-testid="account-delete-error">{error}</p>}
        </div>
      )}
    </section>
  );
}

export default function FreeAccount() {
  const navigate = useNavigate();
  const { email, signOut } = useFreeAuth();

  const handleSignOut = async () => {
    await signOut();
    navigate('/free', { replace: true });
  };

  return (
    <div className="max-w-2xl space-y-8" data-testid="free-account-page">
      <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-none">Account</h1>
      <section className="border border-mono-line bg-mono-card p-6 md:p-8">
        <p className={labelClass}>Signed in as</p>
        <p className="text-white font-body mb-5 break-all" data-testid="account-email">{email}</p>
        <button type="button" onClick={handleSignOut} className={`${btn} bg-white text-black hover:bg-[#E5E5E5] inline-flex items-center gap-2`} data-testid="account-signout-btn">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </section>
      <DeleteAccount email={email} />
    </div>
  );
}
